<template>
  <section class="world-clock">
    <!-- ========== 双时钟区域 ========== -->
    <div class="clock-label">现在的北京时间</div>
    <div class="clock-dual">
      <!-- 1. 极简模拟时钟 -->
      <div class="analog-clock">
        <svg viewBox="0 0 260 260" class="analog-svg">
          <!-- 刻度 -->
          <g v-for="n in 60" :key="n">
            <line
              :x1="130 + (n % 5 === 0 ? 100 : 108) * Math.cos(((n * 6 - 90) * Math.PI) / 180)"
              :y1="130 + (n % 5 === 0 ? 100 : 108) * Math.sin(((n * 6 - 90) * Math.PI) / 180)"
              :x2="130 + 118 * Math.cos(((n * 6 - 90) * Math.PI) / 180)"
              :y2="130 + 118 * Math.sin(((n * 6 - 90) * Math.PI) / 180)"
              :stroke="n % 5 === 0 ? '#333' : '#ddd'"
              :stroke-width="n % 5 === 0 ? 2.5 : 1"
              stroke-linecap="round"
            />
          </g>
          <!-- 数字 -->
          <text
            v-for="n in 12" :key="'num'+n"
            :x="130 + 85 * Math.cos(((n * 30 - 90) * Math.PI) / 180)"
            :y="130 + 85 * Math.sin(((n * 30 - 90) * Math.PI) / 180)"
            text-anchor="middle"
            dominant-baseline="central"
            font-size="18"
            font-weight="600"
            fill="#333"
            font-family="-apple-system, 'PingFang SC', 'Microsoft YaHei', sans-serif"
          >{{ n }}</text>
          <!-- 时针 -->
          <line
            x1="130" y1="130"
            :x2="130 + 65 * Math.cos(((hourAngle - 90) * Math.PI) / 180)"
            :y2="130 + 65 * Math.sin(((hourAngle - 90) * Math.PI) / 180)"
            stroke="#1a1a2e" stroke-width="5" stroke-linecap="round"
          />
          <!-- 分针 -->
          <line
            x1="130" y1="130"
            :x2="130 + 95 * Math.cos(((minuteAngle - 90) * Math.PI) / 180)"
            :y2="130 + 95 * Math.sin(((minuteAngle - 90) * Math.PI) / 180)"
            stroke="#444" stroke-width="3.5" stroke-linecap="round"
          />
          <!-- 秒针 -->
          <line
            x1="130" y1="130"
            :x2="130 + 88 * Math.cos(((secondAngle - 90) * Math.PI) / 180)"
            :y2="130 + 88 * Math.sin(((secondAngle - 90) * Math.PI) / 180)"
            stroke="#e74c3c" stroke-width="1.5" stroke-linecap="round"
          />
          <!-- 中心圆点 -->
          <circle cx="130" cy="130" r="5" fill="#e74c3c" />
          <circle cx="130" cy="130" r="2" fill="#fff" />
        </svg>
      </div>

      <!-- 2. 翻牌时钟 -->
      <div class="flip-clock-wrapper" ref="flipWrapper">
        <div class="flip-clock">
          <template v-for="(d, i) in flipDigits" :key="i">
            <!-- 冒号分隔符 -->
            <div v-if="d.isColon" class="flip-colon">:</div>
            <!-- 翻牌数字（完整显示 + 装饰分割线） -->
            <div v-else class="flip-digit">
              <div class="flip-digit-inner" :key="d.flipKey">
                <span class="flip-digit-num">{{ d.val }}</span>
                <div class="flip-line"></div>
                <div class="flip-dot flip-dot--left"></div>
                <div class="flip-dot flip-dot--right"></div>
              </div>
            </div>
          </template>
        </div>
        <!-- 控件按钮 -->
        <div class="flip-controls">
          <button class="flip-btn" @click="toggleHourMode">{{ is24h ? '24H' : '12H' }}</button>
          <button class="flip-btn" @click="toggleFullscreen">⛶</button>
        </div>
      </div>
    </div>

    <!-- 日期信息 -->
    <div class="clock-date-row">
      <span class="clock-date">{{ beijingDate }} {{ beijingWeekday }}</span>
      <span class="clock-week">第{{ beijingWeek }}周</span>
    </div>
    <div class="clock-sun">
      <span class="sun-item">☀ {{ beijingSunrise }}</span>
      <span class="sun-sep">|</span>
      <span class="sun-item">🌙 {{ beijingSunset }}</span>
      <span class="sun-sep">|</span>
      <span class="sun-item">昼长 {{ beijingDayLength }}</span>
    </div>

    <!-- 全屏翻牌时钟 -->
    <transition name="fs-fade">
      <div v-if="isFullscreen" class="flip-fullscreen" @click="toggleFullscreen">
        <div class="flip-fs-clock">
          <template v-for="(d, i) in flipDigits" :key="'fs' + i">
            <div v-if="d.isColon" class="flip-fs-colon">:</div>
            <div v-else class="flip-fs-digit">
              <span class="flip-fs-num">{{ d.val }}</span>
              <div class="flip-fs-line"></div>
              <div class="flip-fs-dot flip-fs-dot--left"></div>
              <div class="flip-fs-dot flip-fs-dot--right"></div>
            </div>
          </template>
        </div>
        <div class="flip-fs-label">Beijing · {{ is24h ? '24H' : '12H' }}</div>
        <div class="flip-fs-date">{{ beijingDate }} {{ beijingWeekday }}</div>
        <div class="flip-fs-hint">点击任意位置退出</div>
      </div>
    </transition>

    <!-- 全球主要城市时间 -->
    <div class="clock-cities">
      <div class="city-card" v-for="city in cities" :key="city.name">
        <div class="city-name">{{ city.name }}</div>
        <div class="city-time">{{ city.time }}</div>
        <div class="city-date">{{ city.date }} {{ city.weekday }}</div>
        <div class="city-offset">{{ city.offsetStr }}</div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'WorldClock',
  data() {
    return {
      beijingDate: '',
      beijingWeekday: '',
      beijingWeek: 0,
      beijingSunrise: '',
      beijingSunset: '',
      beijingDayLength: '',
      hourAngle: 0,
      minuteAngle: 0,
      secondAngle: 0,
      flipDigits: [],
      is24h: localStorage.getItem('worldclock-is24h') !== 'false',
      isFullscreen: false,
      cities: [
        { name: '东京', tz: 'Asia/Tokyo', time: '', date: '', weekday: '', offsetStr: '' },
        { name: '北京', tz: 'Asia/Shanghai', time: '', date: '', weekday: '', offsetStr: 'UTC+8' },
        { name: '巴黎', tz: 'Europe/Paris', time: '', date: '', weekday: '', offsetStr: '' },
        { name: '伦敦', tz: 'Europe/London', time: '', date: '', weekday: '', offsetStr: '' },
        { name: '纽约', tz: 'America/New_York', time: '', date: '', weekday: '', offsetStr: '' },
        { name: '洛杉矶', tz: 'America/Los_Angeles', time: '', date: '', weekday: '', offsetStr: '' },
        { name: '莫斯科', tz: 'Europe/Moscow', time: '', date: '', weekday: '', offsetStr: '' },
        { name: '迪拜', tz: 'Asia/Dubai', time: '', date: '', weekday: '', offsetStr: '' }
      ],
      timer: null,
      lastSecond: -1,
      flipKeyCounter: 0
    }
  },
  mounted() {
    this.updateAll()
    this.timer = setInterval(this.updateAll, 50)
  },
  beforeUnmount() {
    if (this.timer) clearInterval(this.timer)
    document.removeEventListener('keydown', this.onFullscreenKey)
  },
  methods: {
    updateAll() {
      this.updateAnalogClock()
      this.updateFlipClock()
      const sec = new Date().getSeconds()
      if (sec !== this.lastSecond) {
        this.lastSecond = sec
        this.updateBeijingInfo()
        this.updateSunInfo()
        this.updateCities()
      }
    },
    updateAnalogClock() {
      const now = new Date()
      const beijingNow = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Shanghai' }))
      let h = beijingNow.getHours()
      if (!this.is24h) {
        h = h % 12 || 12
      }
      const m = beijingNow.getMinutes()
      const s = beijingNow.getSeconds()
      const ms = beijingNow.getMilliseconds()
      this.hourAngle = (h + m / 60) * 30
      this.minuteAngle = (m + s / 60) * 6
      this.secondAngle = (s + ms / 1000) * 6
    },
    updateFlipClock() {
      const now = new Date()
      const beijingParts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'Asia/Shanghai',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: !this.is24h
      }).formatToParts(now)

      const hour = beijingParts.find(p => p.type === 'hour')?.value || '00'
      const minute = beijingParts.find(p => p.type === 'minute')?.value || '00'
      const second = beijingParts.find(p => p.type === 'second')?.value || '00'
      const dayPeriod = beijingParts.find(p => p.type === 'dayPeriod')?.value || ''

      let chars = hour.split('').concat([':'], minute.split(''), [':'], second.split(''))

      const newDigits = chars.map(ch => ({
        val: ch,
        isColon: ch === ':',
        isAM: ch === '上午' || ch === 'AM',
        flipKey: 0
      }))

      // 如果不是24h且有AM/PM，附加显示
      if (!this.is24h && dayPeriod) {
        // 简化: 在小时前或后显示AM/PM
      }

      // 空初始状态
      if (this.flipDigits.length === 0) {
        this.flipDigits = newDigits.map(d => ({ ...d, flipKey: this.flipKeyCounter++ }))
        return
      }

      // 检测变化的位并翻转
      const prevDigits = this.flipDigits.filter(d => !d.isColon)

      this.flipDigits = newDigits.map((d, i) => {
        if (d.isColon) return { ...d, flipKey: 0 }
        const prevIdx = i - newDigits.slice(0, i).filter(x => x.isColon).length
        const prev = prevDigits[prevIdx]
        if (prev && prev.val !== d.val) {
          return { ...d, flipKey: this.flipKeyCounter++ }
        }
        // 保持原有 flipKey 不变（不触发动画）
        const oldD = this.flipDigits[i]
        return { ...d, flipKey: oldD ? oldD.flipKey : 0 }
      })
    },
    updateBeijingInfo() {
      const now = new Date()
      const beijing = this.formatDate(now, 'Asia/Shanghai')
      this.beijingDate = beijing.date
      this.beijingWeekday = beijing.weekday
      this.beijingWeek = this.getWeekNumber(now)
    },
    updateSunInfo() {
      const now = new Date()
      const lat = 39.9
      const lng = 116.4
      const tz = 8
      const dayOfYear = this.getDayOfYear(now)
      const { sunrise, sunset } = this.calcSunriseSunset(lat, lng, tz, dayOfYear)
      this.beijingSunrise = sunrise
      this.beijingSunset = sunset
      const srParts = sunrise.split(':')
      const ssParts = sunset.split(':')
      const srMin = parseInt(srParts[0]) * 60 + parseInt(srParts[1])
      const ssMin = parseInt(ssParts[0]) * 60 + parseInt(ssParts[1])
      const diffMin = ssMin - srMin
      this.beijingDayLength = Math.floor(diffMin / 60) + '时' + (diffMin % 60) + '分'
    },
    updateCities() {
      const now = new Date()
      this.cities = this.cities.map(city => {
        const fmt = this.formatDate(now, city.tz)
        return {
          ...city,
          ...fmt,
          offsetStr: city.tz === 'Asia/Shanghai' ? 'UTC+8' : this.getOffsetStr(now, city.tz)
        }
      })
    },
    formatDate(date, timeZone) {
      const hour12 = !this.is24h
      // 使用 formatToParts 精确获取各部分
      const parts = new Intl.DateTimeFormat('zh-CN', {
        timeZone,
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hour12
      }).formatToParts(date)
      const hour = parts.find(p => p.type === 'hour')?.value || '00'
      const minute = parts.find(p => p.type === 'minute')?.value || '00'
      const second = parts.find(p => p.type === 'second')?.value || '00'
      const dayPeriod = parts.find(p => p.type === 'dayPeriod')?.value || ''
      // 手动拼接确保上午/下午在 12H 模式下可见
      const time = hour12 && dayPeriod
        ? `${dayPeriod} ${hour.padStart(2, '0')}:${minute}:${second}`
        : `${hour.padStart(2, '0')}:${minute}:${second}`
      const dateStr = date.toLocaleDateString('zh-CN', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' })
      const weekday = date.toLocaleDateString('zh-CN', { timeZone, weekday: 'long' })
      return { time, date: dateStr, weekday }
    },
    getOffsetStr(date, timeZone) {
      try {
        const parts = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'shortOffset' }).formatToParts(date)
        const offset = parts.find(p => p.type === 'timeZoneName')
        if (offset) return offset.value
      } catch (e) { /* ignore */ }
      return ''
    },
    getWeekNumber(date) {
      const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
      const dayNum = d.getUTCDay() || 7
      d.setUTCDate(d.getUTCDate() + 4 - dayNum)
      const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
      return Math.ceil(((d - yearStart) / 86400000 + 1) / 7)
    },
    getDayOfYear(date) {
      const start = new Date(date.getFullYear(), 0, 0)
      const diff = date - start
      return Math.floor(diff / 86400000)
    },
    calcSunriseSunset(lat, lng, tz, dayOfYear) {
      const B = 360 / 365 * (dayOfYear - 81)
      const BRad = B * Math.PI / 180
      const eqTime = 9.87 * Math.sin(2 * BRad) - 7.53 * Math.cos(BRad) - 1.5 * Math.sin(BRad)
      const declination = 23.45 * Math.sin(BRad) * Math.PI / 180
      const latRad = lat * Math.PI / 180
      const cosH = (Math.cos(90.833 * Math.PI / 180) - Math.sin(latRad) * Math.sin(declination)) / (Math.cos(latRad) * Math.cos(declination))
      const H = Math.acos(Math.max(-1, Math.min(1, cosH))) * 180 / Math.PI
      const sunriseUTC = 720 - 4 * (lng + H) - eqTime
      const sunsetUTC = 720 - 4 * (lng - H) - eqTime
      const sunriseLocal = sunriseUTC + tz * 60
      const sunsetLocal = sunsetUTC + tz * 60
      return {
        sunrise: this.minToTimeStr(sunriseLocal),
        sunset: this.minToTimeStr(sunsetLocal)
      }
    },
    minToTimeStr(minutes) {
      let m = Math.round(minutes)
      while (m < 0) m += 1440
      m = m % 1440
      const h = Math.floor(m / 60)
      const min = m % 60
      return String(h).padStart(2, '0') + ':' + String(min).padStart(2, '0')
    },
    toggleHourMode() {
      this.is24h = !this.is24h
      localStorage.setItem('worldclock-is24h', String(this.is24h))
      window.dispatchEvent(new CustomEvent('worldclock-24h-changed', { detail: this.is24h }))
      this.lastSecond = -1
      this.updateCities()
      this.updateFlipClock()
    },
    toggleFullscreen() {
      if (!this.isFullscreen) {
        document.addEventListener('keydown', this.onFullscreenKey)
      } else {
        document.removeEventListener('keydown', this.onFullscreenKey)
      }
      this.isFullscreen = !this.isFullscreen
    },
    onFullscreenKey(e) {
      if (e.key === 'Escape') this.toggleFullscreen()
    }
  }
}
</script>

<style scoped>
.world-clock {
  max-width: 1100px;
  margin: 0 auto 20px;
  padding: 0 16px;
}

/* ========== 标签 ========== */
.clock-label {
  text-align: center;
  font-size: 14px;
  color: #888;
  letter-spacing: 1px;
  margin-bottom: 12px;
  padding-top: 4px;
}

/* ========== 双时钟布局 ========== */
.clock-dual {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 40px;
  background: #fff;
  border-radius: 16px;
  padding: 30px 24px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  flex-wrap: wrap;
}

/* ========== 1. 模拟时钟 ========== */
.analog-clock {
  flex-shrink: 0;
}
.analog-svg {
  width: 220px;
  height: 220px;
  display: block;
}

/* ========== 2. 翻牌时钟 ========== */
.flip-clock-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}
.flip-clock {
  display: flex;
  align-items: center;
  gap: 6px;
}

/* 翻牌数字外壳 */
.flip-digit {
  perspective: 400px;
}
.flip-digit-inner {
  position: relative;
  width: 68px;
  height: 90px;
  background: #1a1a2e;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

/* 数字文字 */
.flip-digit-num {
  font-size: 68px;
  font-weight: 700;
  font-family: 'SF Mono', 'Consolas', 'Menlo', 'Courier New', monospace;
  color: #fff;
  line-height: 1;
  font-feature-settings: 'tnum';
  font-variant-numeric: tabular-nums;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

/* 水平装饰线 */
.flip-line {
  position: absolute;
  top: 50%;
  left: 3px;
  right: 3px;
  height: 1px;
  background: rgba(0, 0, 0, 0.5);
  z-index: 2;
}

/* 装饰线两侧圆点 */
.flip-dot {
  position: absolute;
  top: 50%;
  width: 7px;
  height: 7px;
  background: #0a0a0a;
  border-radius: 50%;
  transform: translateY(-50%);
  z-index: 3;
}
.flip-dot--left { left: -3.5px; }
.flip-dot--right { right: -3.5px; }

/* 翻牌动画 */
.flip-digit-inner {
  animation: flipCard 0.5s ease-in-out;
}
@keyframes flipCard {
  0%   { transform: rotateX(0deg); }
  40%  { transform: rotateX(-90deg); }
  60%  { transform: rotateX(-90deg); }
  100% { transform: rotateX(0deg); }
}

/* 冒号 */
.flip-colon {
  font-size: 48px;
  font-weight: 700;
  color: #333;
  line-height: 90px;
  padding: 0 1px;
  animation: colonBlink 1s step-end infinite;
}
@keyframes colonBlink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.2; }
}

/* 控件按钮 */
.flip-controls {
  display: flex;
  gap: 8px;
}
.flip-btn {
  padding: 5px 12px;
  font-size: 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fafafa;
  color: #666;
  cursor: pointer;
  transition: all 0.15s;
}
.flip-btn:hover {
  background: #f0f0f0;
  border-color: #bbb;
  color: #333;
}

/* ========== 日期 / 日出日落 ========== */
.clock-date-row {
  margin-top: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  flex-wrap: wrap;
}
.clock-date {
  font-size: 15px;
  color: #666;
}
.clock-week {
  font-size: 13px;
  background: #f0f0f0;
  color: #888;
  padding: 2px 10px;
  border-radius: 20px;
}
.clock-sun {
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
}
.sun-item { font-size: 13px; color: #999; }
.sun-sep { color: #ddd; }

/* ========== 全屏翻牌时钟 ========== */
.flip-fullscreen {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: #0a0a0a;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  user-select: none;
}
.flip-fs-clock {
  display: flex;
  align-items: center;
  gap: 12px;
}

.flip-fs-digit {
  position: relative;
  width: 140px;
  height: 180px;
  background: #181818;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.flip-fs-num {
  font-size: 140px;
  font-weight: 700;
  font-family: 'SF Mono', 'Consolas', 'Menlo', 'Courier New', monospace;
  color: #f0f0f0;
  line-height: 1;
  font-feature-settings: 'tnum';
  font-variant-numeric: tabular-nums;
}
.flip-fs-line {
  position: absolute;
  top: 50%;
  left: 6px;
  right: 6px;
  height: 2px;
  background: rgba(0, 0, 0, 0.6);
  z-index: 2;
}
.flip-fs-dot {
  position: absolute;
  top: 50%;
  width: 14px;
  height: 14px;
  background: #000;
  border-radius: 50%;
  transform: translateY(-50%);
  z-index: 3;
}
.flip-fs-dot--left { left: -7px; }
.flip-fs-dot--right { right: -7px; }

.flip-fs-colon {
  font-size: 110px;
  font-weight: 700;
  color: #f0f0f0;
  line-height: 180px;
  padding: 0 6px;
  animation: colonBlink 1s step-end infinite;
}

.flip-fs-label {
  margin-top: 30px;
  font-size: 20px;
  color: #666;
  letter-spacing: 3px;
}
.flip-fs-date {
  margin-top: 8px;
  font-size: 16px;
  color: #555;
}
.flip-fs-hint {
  position: absolute;
  bottom: 30px;
  font-size: 13px;
  color: #444;
  opacity: 0.6;
}

.fs-fade-enter-active { transition: opacity 0.3s; }
.fs-fade-leave-active { transition: opacity 0.2s; }
.fs-fade-enter-from, .fs-fade-leave-to { opacity: 0; }

/* ========== 全球城市卡片 ========== */
.clock-cities {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-top: 16px;
}
.city-card {
  background: #fff;
  border-radius: 12px;
  padding: 20px 16px;
  text-align: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s, box-shadow 0.2s;
}
.city-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}
.city-name { font-size: 14px; font-weight: 600; color: #333; margin-bottom: 6px; }
.city-time {
  font-size: 26px; font-weight: 300;
  font-family: 'SF Mono', 'Consolas', 'Menlo', 'Courier New', monospace;
  color: #1a1a2e; letter-spacing: 1px;
}
.city-date { font-size: 11px; color: #999; margin-top: 4px; }
.city-offset { font-size: 11px; color: #1677ff; margin-top: 2px; font-weight: 600; }

/* ========== 响应式 ========== */
@media (max-width: 768px) {
  .clock-dual {
    flex-direction: column;
    gap: 24px;
    padding: 24px 16px;
  }
  .analog-svg {
    width: 180px;
    height: 180px;
  }
  .flip-digit-inner {
    width: 50px;
    height: 68px;
  }
  .flip-digit-num {
    font-size: 50px;
  }
  .flip-colon {
    font-size: 36px;
    line-height: 68px;
  }
  .flip-clock { gap: 4px; }

  .flip-fs-digit { width: 80px; height: 110px; }
  .flip-fs-num { font-size: 86px; }
  .flip-fs-colon { font-size: 64px; line-height: 110px; }

  .clock-cities { grid-template-columns: repeat(2, 1fr); gap: 8px; }
  .city-card { padding: 14px 10px; }
  .city-time { font-size: 22px; }
  .city-name { font-size: 13px; }
}

@media (max-width: 480px) {
  .analog-svg {
    width: 140px;
    height: 140px;
  }
  .flip-digit-inner {
    width: 38px;
    height: 52px;
    border-radius: 5px;
  }
  .flip-digit-num {
    font-size: 38px;
  }
  .flip-colon {
    font-size: 26px;
    line-height: 52px;
  }
  .flip-clock { gap: 2px; }
  .flip-clock-wrapper { gap: 8px; }
  .flip-btn { padding: 4px 10px; font-size: 11px; }

  .flip-fs-digit { width: 58px; height: 82px; border-radius: 8px; }
  .flip-fs-num { font-size: 62px; }
  .flip-fs-colon { font-size: 46px; line-height: 82px; }
  .flip-fs-clock { gap: 6px; }

  .clock-date { font-size: 13px; }
  .city-card { padding: 12px 8px; }
  .city-time { font-size: 20px; }
}
</style>
